import { DocumentNode } from 'graphql';
import { GET_TTD_OTS_SEGMENTS } from '../../../graphql/queries';
import { NA, OTS_SGM_MNG_SEGMENT_NAME } from '../../../content';
import {
  Routes,
  SEGMENT_MANAGER_COLUMN_KEYS,
  SEGMENT_MANAGER_GRAPHQL_ENDPOINTS,
} from '../../../constants';
import { getTtdColumns } from '../constants';
import { isNil } from 'lodash';
import type { ReactNode } from 'react';

/**
 * Utility function to get GraphQL query endpoint name based on platform type
 * @param platform - The platform type (e.g., 'TTD', 'META', etc.)
 * @returns The GraphQL query endpoint name for the platform
 */
export const getGraphQLEndpointByPlatform = (platform: string): string => {
  switch (platform) {
    case 'TTD':
      return SEGMENT_MANAGER_GRAPHQL_ENDPOINTS.TTD;
    // Add more platform cases here as needed
    // Example for other platforms:
    // case 'META':
    //   return SEGMENT_MANAGER_GRAPHQL_ENDPOINTS.META;
    // case 'GOOGLE':
    //   return SEGMENT_MANAGER_GRAPHQL_ENDPOINTS.GOOGLE;
    default:
      // Default to TTD if platform is not recognized
      return SEGMENT_MANAGER_GRAPHQL_ENDPOINTS.TTD;
  }
};

export const getGraphQLQueryByPlatform = (platform: string): DocumentNode => {
  switch (platform) {
    case 'TTD':
      return GET_TTD_OTS_SEGMENTS;
    default:
      return GET_TTD_OTS_SEGMENTS;
  }
};

/**
 * Utility function to get DataTable columns configuration based on platform type
 * @param platform - The platform type (e.g., 'TTD', 'META', etc.)
 * @param sortBy - Current sort column
 * @param sortOrder - Current sort order ('ASC' | 'DESC')
 * @param selectColumnHeader - Optional header node for the selection column (e.g. master checkbox)
 * @param includeSelectColumn - When false, the checkbox column is omitted (LaunchDarkly `uiSegmentManagerSetBuyableEnabled`).
 * @returns Array of column configurations for the DataTable
 */
export const getColumnsByPlatform = (
  platform: string,
  sortBy: string,
  sortOrder: 'ASC' | 'DESC',
  selectColumnHeader?: ReactNode,
  includeSelectColumn = true
) => {
  const selectCol = includeSelectColumn
    ? [
        {
          title: selectColumnHeader ?? '',
          column: SEGMENT_MANAGER_COLUMN_KEYS.TTD.SELECT,
          width: 52,
          isSortDisabled: true,
        },
      ]
    : [];

  const baseColumns = [
    ...selectCol,
    {
      title: OTS_SGM_MNG_SEGMENT_NAME,
      column: SEGMENT_MANAGER_COLUMN_KEYS.TTD.SEGMENT_NAME,
      width: '18%',
      isSorted: sortBy === SEGMENT_MANAGER_COLUMN_KEYS.TTD.SEGMENT_NAME,
      sortOrder:
        sortBy === SEGMENT_MANAGER_COLUMN_KEYS.TTD.SEGMENT_NAME
          ? sortOrder
          : undefined,
    },
  ];

  // Platform-specific columns
  switch (platform) {
    case 'TTD':
      return [...baseColumns, ...getTtdColumns(sortBy, sortOrder)];

    // Add more platform cases here as needed
    // Example for other platforms:
    // case 'META':
    //   return [...baseColumns, ...getMetaColumns(sortBy, sortOrder)];

    default:
      // Default to TTD columns if platform is not recognized
      return [...baseColumns, ...getTtdColumns(sortBy, sortOrder)];
  }
};

/**
 * Formats a number with K/M notation
 * @param value - The numeric value to format
 * @returns Formatted string (e.g., "1K", "1.5M", "672") or "N/A" if value is null/undefined
 */
export const formatNumber = (value: number | null | undefined): string => {
  // Use lodash isNil to check for null or undefined (0 is valid)
  if (isNil(value)) {
    return NA;
  }

  // Round to clean integer first to avoid floating point errors
  const roundedValue = Math.round(value);

  if (roundedValue >= 1000000) {
    const millions = roundedValue / 1000000;
    // Show decimal only if needed
    return millions % 1 === 0 ? `${millions}M` : `${millions.toFixed(1)}M`;
  }

  if (roundedValue >= 1000) {
    const thousands = roundedValue / 1000;
    // Show decimal only if needed
    return thousands % 1 === 0 ? `${thousands}K` : `${thousands.toFixed(1)}K`;
  }

  return roundedValue.toString();
};

/**
 * Formats a number as currency with K/M notation
 * @param value - The numeric value to format
 * @returns Formatted currency string (e.g., "$1K", "$1.5M", "$672") or "N/A" if value is null/undefined
 */
export const formatCurrency = (value: number | null | undefined): string => {
  // Use lodash isNil to check for null or undefined (0 is valid)
  if (isNil(value)) {
    return NA;
  }

  // Round to avoid floating point errors
  const roundedValue = Math.round(value);

  if (roundedValue >= 1000000) {
    const millions = roundedValue / 1000000;
    return millions % 1 === 0 ? `$${millions}M` : `$${millions.toFixed(1)}M`;
  }

  if (roundedValue >= 1000) {
    const thousands = roundedValue / 1000;
    return thousands % 1 === 0 ? `$${thousands}K` : `$${thousands.toFixed(1)}K`;
  }

  return `$${roundedValue}`;
};

/**
 * Format number to millions with one decimal place
 * @param num - Number to format
 * @returns Formatted string like "15.9M" or "N/A" if value is null/undefined
 */
export const formatToMillions = (num: number | null | undefined): string => {
  // Use lodash isNil to check for null or undefined (0 is valid)
  if (isNil(num)) {
    return 'N/A';
  }

  // 0 is a valid value
  if (num === 0) return '0';

  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(2)}M`;
  } else if (num >= 1000) {
    return `${(num / 1000).toFixed(2)}K`;
  }
  return num.toFixed(2);
};

/**
 * Returns the audience ID to use for the "Edit Audience" button based on active tab.
 * Overview tab uses the primary audienceId; Remodeling tab uses audienceIdForRemodeling when available, else audienceId.
 * @param activeTab - Current tab ('overview' | 'remodeling')
 * @param segmentDetail - Segment detail with audienceId and optional audienceIdForRemodeling
 * @returns The audience ID to open in Audience Studio, or null/undefined if none
 */
export const getEffectiveAudienceId = (
  activeTab: string,
  segmentDetail?: {
    audienceId?: string | null;
    audienceIdForRemodeling?: string | null;
  } | null
): string | null | undefined => {
  if (!segmentDetail) return undefined;
  if (activeTab === 'remodeling') {
    return (
      segmentDetail.audienceIdForRemodeling ??
      segmentDetail.audienceId ??
      undefined
    );
  }
  return segmentDetail.audienceId ?? undefined;
};

/**
 * Builds a segment detail URL with optional tab and query parameters
 * @param params - Object containing platform, segmentId, queryString, and optional tab
 * @returns Complete URL path for segment detail page
 * @example
 * buildSegmentDetailUrl({ platform: 'TTD', segmentId: 'abc123', queryString: 'rateLevel=System' })
 * // Returns: '/main/segment-manager/TTD/abc123?rateLevel=System'
 * @example
 * buildSegmentDetailUrl({ platform: 'TTD', segmentId: 'abc123', queryString: '', tab: 'remodeling' })
 * // Returns: '/main/segment-manager/TTD/abc123/remodeling'
 */
export const buildSegmentDetailUrl = ({
  platform,
  segmentId,
  queryString,
  tab,
}: {
  platform: string;
  segmentId: string;
  queryString: string;
  tab?: string;
}): string => {
  const baseUrl = `${Routes.SEGMENT_MANAGER}/${platform}/${segmentId}`;
  const tabPath = tab ? `/${tab}` : '';
  const query = queryString ? `?${queryString}` : '';
  return `${baseUrl}${tabPath}${query}`;
};
